<template>
  <div :id="`${source}_add_voicemail`">
    <CustomModal
      title="Add Voicemail"
      width="460px"
      :visible="visible"
      @update:visible="$emit('update:visible', $event)"
    >
      <div class="flex flex-col gap-6 proxima_nova secondary_color text-[13px]">
        <div id="voicemail_name" class="form_col">
          <span>Voicemail Name</span>
          <a-input
            class="input_medium"
            v-model:value="formState.name"
            placeholder="Enter voicemail name"
            ref="voicemailNameInputRef"
            allow-clear
          />
        </div>

        <div id="voicemail_section" class="flex flex-col items-center gap-2">
          <div v-if="formState.audio" class="flex flex-col w-full gap-2">
            <div
              class="flex flex-row items-center gap-3 justify-between p-3 bg_white_4 rounded-lg w-full"
            >
              <AudioPlayer
                :source="source"
                :audio-url="audioUrl"
                :type="formState.audio?.type"
              />

              <span
                class="text-sm text_color font-semibold cursor-pointer"
                @click="handleDeleteClick"
                >Delete</span
              >
            </div>
          </div>

          <div v-if="!formState.audio" class="w-full">
            <div id="upload_voicemail" class="form_col w-full">
              <span>Voicemail file</span>
              <UploadComponent
                :source="source"
                type="audio"
                description="Only .mp3, .wav, or .ogg audio files are supported"
                v-model="formState.audio"
                @update:modelValue="handleFileSelection"
              />
            </div>

            <div class="flex justify-center my-2">
              <span class="secondary_color">OR</span>
            </div>

            <div id="record_voicemail" class="form_col w-full">
              <span>Record voicemail</span>
              <Recorder
                :source="source"
                type="variant1"
                @recordingComplete="handleFileSelection"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <a-button
          class="primary_medium"
          @click="handleSubmit"
          :disabled="isSubmitDisabled"
          :loading="formState.isLoading"
        >
          {{ formState.isLoading ? "Adding" : "Add" }}
        </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, nextTick } from "vue";
import { message } from "ant-design-vue";
import CallService from "@/src/api/CallService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import UploadComponent from "@/src/components/upload_components/UploadComponent.vue";
import Recorder from "@/src/components/media_components/Recorder.vue";
import AudioPlayer from "@/src/components/AudioPlayer.vue";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:visible", "update:success"]);

const formState = ref({
  name: "Untitled voicemail",
  audio: null,
  isLoading: false,
});
const voicemailNameInputRef = ref(null);
const audioUrl = ref(null);

const isSubmitDisabled = computed(() => {
  const { name, audio, isLoading } = formState.value;
  return !audio || !name || name.trim() === "" || isLoading;
});

const createAudioUrl = (file) => {
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value);
  }
  if (file) {
    audioUrl.value = URL.createObjectURL(file);
  } else {
    audioUrl.value = null;
  }
};

const handleFileSelection = (file) => {
  formState.value.audio = file;
  createAudioUrl(file);
};

const handleDeleteClick = () => {
  formState.value.audio = null;
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value);
    audioUrl.value = null;
  }
};

const resetForm = () => {
  formState.value = {
    name: "Untitled voicemail",
    audio: null,
    isLoading: false,
  };
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value);
    audioUrl.value = null;
  }
};

const handleSubmit = async () => {
  try {
    formState.value.isLoading = true;
    const formData = new FormData();
    formData.append("name", formState.value.name);
    formData.append("file", formState.value.audio);

    const response = await CallService.createVoicemail(formData);

    if (response?.success) {
      emit("update:visible", false);
      emit("update:success", response?.data);
      message.success("Voicemail successfully added!");
      resetForm();
    }
  } catch (error) {
    console.error("Error creating voicemail:", error);
    message.error(
      error?.response?.data?.error ||
        "An error occurred while creating voicemail",
    );
  } finally {
    formState.value.isLoading = false;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      resetForm();
    } else {
      nextTick(() => {
        if (voicemailNameInputRef?.value) {
          voicemailNameInputRef.value?.focus();
        }
      });
    }
  },
);

onBeforeUnmount(() => {
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value);
  }
});
</script>
